import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import api from 'api/client';

const borderColor = 'rgba(245, 158, 11, 0.35)';

const cardSx = {
  border: '1px solid',
  borderColor,
  borderRadius: 3,
  boxShadow: 'none'
};

const actionButtonSx = {
  bgcolor: 'var(--fz-surface-1, #111827)',
  color: '#f59e0b',
  border: '1px solid #f59e0b',
  '&:hover': { bgcolor: 'var(--fz-surface-0, #0b1220)', borderColor: '#f59e0b' }
};

function parseError(error, fallback) {
  const payload = error?.response?.data;
  if (!payload) return error?.message || fallback;
  if (typeof payload === 'string') return payload;
  if (payload.detail) return payload.detail;
  return fallback;
}

const defaultForm = {
  enabled: false,
  banner_enabled: true,
  message: 'Scheduled platform maintenance is in progress.',
  allowed_ips_text: ''
};

const AdminMaintenancePage = () => {
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/api/admin/system/maintenance/');
      const allowedIps = Array.isArray(data?.allowed_ips) ? data.allowed_ips : [];
      setForm({
        enabled: Boolean(data?.enabled),
        banner_enabled: Boolean(data?.banner_enabled),
        message: data?.message || defaultForm.message,
        allowed_ips_text: allowedIps.join(', ')
      });
    } catch (err) {
      setError(parseError(err, 'Failed to load maintenance settings.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const allowedIpsPreview = useMemo(
    () => form.allowed_ips_text.split(',').map((item) => item.trim()).filter(Boolean),
    [form.allowed_ips_text]
  );

  const save = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.post('/api/admin/system/maintenance/', {
        enabled: form.enabled,
        banner_enabled: form.banner_enabled,
        message: form.message,
        allowed_ips: allowedIpsPreview
      });
      setSuccess('Maintenance configuration saved.');
      await load();
    } catch (err) {
      setError(parseError(err, 'Failed to save maintenance settings.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2.5}>
      <Card sx={cardSx}>
        <CardContent sx={{ py: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" spacing={1.5}>
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Avatar sx={{ bgcolor: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', width: 42, height: 42 }}>
                <BuildCircleOutlinedIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Maintenance & Banner Control
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure maintenance state and the global user-facing service banner.
                </Typography>
              </Box>
            </Stack>
            <Button variant="contained" onClick={save} disabled={loading || saving} sx={actionButtonSx}>
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {(loading || saving) && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 7 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Maintenance Mode
                  </Typography>
                  <Switch
                    checked={form.enabled}
                    onChange={(event) => setForm((prev) => ({ ...prev, enabled: event.target.checked }))}
                    disabled={loading || saving}
                  />
                </Stack>

                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Show Banner
                  </Typography>
                  <Switch
                    checked={form.banner_enabled}
                    onChange={(event) => setForm((prev) => ({ ...prev, banner_enabled: event.target.checked }))}
                    disabled={loading || saving}
                  />
                </Stack>

                <TextField
                  label="Maintenance Message"
                  value={form.message}
                  onChange={(event) => setForm((prev) => ({ ...prev, message: event.target.value }))}
                  multiline
                  minRows={3}
                  fullWidth
                  size="small"
                />

                <TextField
                  label="Allowed IPs (comma separated)"
                  placeholder="127.0.0.1, 10.0.0.12"
                  value={form.allowed_ips_text}
                  onChange={(event) => setForm((prev) => ({ ...prev, allowed_ips_text: event.target.value }))}
                  fullWidth
                  size="small"
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 5 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Preview
              </Typography>
              <Alert severity={form.enabled ? 'warning' : 'info'}>
                {form.enabled ? form.message : 'Maintenance mode is currently disabled.'}
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Allowed IP Count: <strong>{allowedIpsPreview.length}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Banner Visibility: <strong>{form.banner_enabled ? 'Enabled' : 'Hidden'}</strong>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default AdminMaintenancePage;
